import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Check, Clock, Edit, UserCheck, Building2, BriefcaseBusiness, UserRound, Phone, Mail, UsersRound, Share2, MapPin, FileText, CalendarDays } from "lucide-react";
import { Lead, Interaction } from "../../types/crm";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import whatsappLogo from "../../assets/whatsapp-icon.png";
import { getLocalStorageItem } from "../../utils/Validation";

interface UpdateLeadModalProps {
  lead: Lead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdateLead: (lead: Lead) => void;
  rmList: { id: number; name: string }[];
  onLeadAdded?: () => void;
  statusOptions: { id: number; value: string }[];
}

const UpdateLeadModal = ({
  lead,
  open,
  onOpenChange,
  onUpdateLead,
  rmList,
  onLeadAdded,
  statusOptions,
}: UpdateLeadModalProps) => {
  const [formData, setFormData] = useState({
    leadStatusId: undefined as number | undefined,
    remark: "",
    nextFollowUp: undefined as Date | undefined,
    leadType: "",
  });

  const [editableData, setEditableData] = useState({
    companyName: "",
    designation: "",
    name: "",
    mobile: "",
    email: "",
    rmleadId: 0,
    leadSource: "",
    location: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const fieldIconClass = "pointer-events-none absolute left-3 top-1/2 z-20 h-3.5 w-3.5 -translate-y-1/2 text-[#9aa7ba]";
  const fieldInputClass = "h-9 rounded-[11px] border-slate-200 pl-9 text-[13px] text-slate-700 placeholder:text-slate-400 focus:border-[#3f8b98] focus:ring-[#3f8b98]/20";
  const fieldSelectClass = "h-9 rounded-[11px] border-slate-200 pl-9 pr-9 text-[13px] text-slate-700 focus:border-[#3f8b98] focus:ring-[#3f8b98]/20";
  const fieldTextareaClass = "rounded-[11px] border-slate-200 text-[13px] text-slate-700 placeholder:text-slate-400 focus:border-[#3f8b98] focus:ring-[#3f8b98]/20 pl-9";

  useEffect(() => {
    if (!lead || !open) return;

    setEditableData({
      companyName: lead.companyName,
      designation: lead.designation,
      name: lead.name,
      mobile: lead.mobile || "",
      email: lead.email || "",
      rmleadId: lead.rmleadId || 0,
      leadSource: lead.leadSource,
      location: lead.location || "",
    });

    setFormData({
      leadStatusId: lead.leadStatusId,
      remark: "",
      nextFollowUp: undefined,
      leadType: lead.leadType,
    });

    setIsEditing(false);
  }, [lead, open]);

  const getStatusValue = (leadStatusId: number) => {
    const statusObj = statusOptions.find((opt) => opt.id === leadStatusId);
    return statusObj ? statusObj.value : "New";
  };

  const mandatoryFollowUpStatusIds = [4, 6, 8];
  const isFollowUpDateRequired = () => mandatoryFollowUpStatusIds.includes(formData.leadStatusId ?? -1);

  const handleLeadSave = async () => {
    if (!lead) return;

    try {
      const updatedLead: Lead = {
        ...lead,
        ...editableData,
        leadStatusId: formData.leadStatusId!,
        leadType: formData.leadType,
        nextFollowUp: formData.nextFollowUp?.toISOString() || "",
        interactions: lead.interactions,
      };

      const response: any = await apiService.put(`/leads/update`, {
        ...((updatedLead as unknown) as Record<string, unknown>),
      });

      if (response?.status === 200) {
        toast.success("Lead updated successfully");
        onUpdateLead?.(updatedLead);
      } else {
        toast.error(response?.message || "Failed to update lead");
      }
    } catch (e: any) {
      console.error("Error updating lead:", e);
      toast.error(e.message || "Something went wrong. Please try again later.");
    }
  };

  const handleSave = async () => {
    if (isSaving || !lead) return;

    if (!formData.remark || formData.remark.trim() === "") {
      toast.error("Remark is required.");
      return;
    }

    if (!formData.leadStatusId || formData.leadStatusId === 1) {
      toast.error("Please select a status.");
      return;
    }

    if (isFollowUpDateRequired() && !formData.nextFollowUp) {
      toast.error("Next Follow-up Date is required for this status.");
      return;
    }

    setIsSaving(true);

    const userDetails = getLocalStorageItem("detail");
    const firstName = userDetails?.firstName || userDetails?.name?.split(" ")[0] || "";

    const newInteraction: Omit<Interaction, "id"> = {
      date: new Date().toISOString(),
      leadStatusId: formData.leadStatusId!,
      status: getStatusValue(formData.leadStatusId!),
      reason: "",
      remark: formData.remark,
      nextFollowUp: formData.nextFollowUp?.toISOString() || "",
      firstName,
    };

    const updatedLead: Lead = {
      ...lead,
      ...editableData,
      leadStatusId: formData.leadStatusId!,
      leadType: formData.leadType,
      nextFollowUp: formData.nextFollowUp?.toISOString() || "",
      interactions: [newInteraction as Interaction, ...(lead.interactions || [])],
    };

    try {
      const response: any = await apiService.put(`/leads/update`, {
        ...((updatedLead as unknown) as Record<string, unknown>),
      });

      if (response?.status === 200) {
        toast.success("Lead updated successfully");
        onUpdateLead?.(updatedLead);
        onLeadAdded?.();
        onOpenChange(false);
      } else {
        toast.error(response?.message || "Failed to update lead");
      }
    } catch (e: any) {
      console.error("Error updating lead:", e);
      toast.error(e.message || "Something went wrong. Please try again later.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableData((prev) => ({ ...prev, mobile: e.target.value }));
  };

  const handleBlur = () => {
    if (editableData.mobile.length !== 10) {
      setError("Mobile number must be exactly 10 digits");
    } else {
      setError("");
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[55vw] !max-w-[55vw] sm:!max-w-[55vw] overflow-hidden rounded-[24px] border border-slate-200 bg-white p-0 shadow-[0_20px_56px_rgba(15,23,42,0.2)]">
        <div className="border-b border-slate-200 px-5 py-4">
          <DialogTitle className="text-[20px] font-semibold tracking-[-0.04em] text-slate-900">
            Update Lead
          </DialogTitle>
        </div>

        <div className="max-h-[84vh] overflow-y-auto px-4 py-0 mb-6">
          <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
            <Card className="overflow-hidden rounded-[18px] border border-slate-200 shadow-none">
              <CardHeader className="border-b border-slate-200 bg-white px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-[16px] font-semibold text-slate-900">
                    Lead Information
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (isEditing) {
                        if (editableData.mobile.length !== 10) {
                          alert("Mobile number must be exactly 10 digits");
                          return;
                        }
                        handleLeadSave();
                      }
                      setIsEditing(!isEditing);
                    }}
                    className="h-8 rounded-[11px] border-slate-200 bg-white px-3 text-[12px] text-slate-600 hover:bg-slate-50"
                  >
                    <Edit className="mr-2 h-3.5 w-3.5" />
                    {isEditing ? "Save" : "Edit"}
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="px-4 py-3.5">
                <div className="grid gap-x-3 gap-y-3 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="companyName" className="text-[11px] font-medium text-slate-700">
                      Company Name
                    </Label>
                    <div className="relative">
                      <Building2 className={fieldIconClass} />
                      <Input
                        id="companyName"
                        value={editableData.companyName}
                        onChange={(e) => setEditableData((prev) => ({ ...prev, companyName: e.target.value }))}
                        className={fieldInputClass}
                        readOnly={!isEditing}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="designation" className="text-[11px] font-medium text-slate-700">
                      Designation
                    </Label>
                    <div className="relative">
                      <BriefcaseBusiness className={fieldIconClass} />
                      <Input
                        id="designation"
                        value={editableData.designation}
                        onChange={(e) => setEditableData((prev) => ({ ...prev, designation: e.target.value }))}
                        className={fieldInputClass}
                        readOnly={!isEditing}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-[11px] font-medium text-slate-700">
                      Name
                    </Label>
                    <div className="relative">
                      <UserRound className={fieldIconClass} />
                      <Input
                        id="name"
                        value={editableData.name}
                        onChange={(e) => setEditableData((prev) => ({ ...prev, name: e.target.value }))}
                        className={fieldInputClass}
                        readOnly={!isEditing}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="mobile" className="text-[11px] font-medium text-slate-700">
                      Mobile Number
                    </Label>
                    <div className="relative">
                      <Phone className={fieldIconClass} />
                      <Input
                        id="mobile"
                        type="tel"
                        value={editableData.mobile}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${fieldInputClass} ${error ? "border-red-500" : ""}`}
                        readOnly={!isEditing}
                      />
                    </div>
                    {error && <p className="text-[11px] text-red-500">{error}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-[11px] font-medium text-slate-700">
                      Email
                    </Label>
                    <div className="relative">
                      <Mail className={fieldIconClass} />
                      <Input
                        id="email"
                        type="email"
                        value={editableData.email}
                        onChange={(e) => setEditableData((prev) => ({ ...prev, email: e.target.value }))}
                        className={fieldInputClass}
                        readOnly={!isEditing}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="leadRM" className="text-[11px] font-medium text-slate-700">
                      Lead RM
                    </Label>
                    {isEditing ? (
                      <div className="relative">
                        <UsersRound className={fieldIconClass} />
                        <Select
                          value={String(editableData.rmleadId)}
                          onValueChange={(value) => setEditableData((prev) => ({ ...prev, rmleadId: Number(value) }))}
                        >
                          <SelectTrigger className={fieldSelectClass}>
                            <SelectValue placeholder="Select RM" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto bg-white">
                            {rmList.map((rm) => (
                              <SelectItem key={rm.id} value={String(rm.id)}>
                                {rm.name.trim()}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div className="relative">
                        <UsersRound className={fieldIconClass} />
                        <Input
                          value={rmList.find((rm) => rm.id === Number(editableData.rmleadId))?.name.trim() || "N/A"}
                          className={fieldInputClass}
                          readOnly
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="location" className="text-[11px] font-medium text-slate-700">
                      Location
                    </Label>
                    <div className="relative">
                      <MapPin className={fieldIconClass} />
                      <Input
                        id="location"
                        type="text"
                        value={editableData.location}
                        onChange={(e) => setEditableData((prev) => ({ ...prev, location: e.target.value }))}
                        className={fieldInputClass}
                        readOnly={!isEditing}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="leadSource" className="text-[11px] font-medium text-slate-700">
                      Lead Source
                    </Label>
                    {isEditing ? (
                      <div className="relative">
                        <Share2 className={fieldIconClass} />
                        <Select
                          value={String(editableData.leadSource)}
                          onValueChange={(value) => setEditableData((prev) => ({ ...prev, leadSource: String(value) }))}
                        >
                          <SelectTrigger className={fieldSelectClass}>
                            <SelectValue placeholder="Select lead source" />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="Website">Website</SelectItem>
                            <SelectItem value="Social Media">Social Media</SelectItem>
                            <SelectItem value="Referral">Referral</SelectItem>
                            <SelectItem value="Cold Call">Cold Call</SelectItem>
                            <SelectItem value="Email Campaign">Email Campaign</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div className="relative">
                        <Share2 className={fieldIconClass} />
                        <Input
                          value={editableData.leadSource}
                          className={fieldInputClass}
                          readOnly
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3 text-[11px] text-slate-500">
                  <span>Created: {new Date(lead.createdAt).toLocaleDateString("en-GB")}</span>
                  <a
                    href={`https://wa.me/${editableData.mobile}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 transition-colors hover:bg-emerald-600"
                  >
                    <img src={whatsappLogo} alt="WhatsApp" className="h-4.5 w-4.5 object-contain" />
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden rounded-[18px] border border-slate-200 shadow-none">
              <CardHeader className="border-b border-slate-200 bg-white px-4 py-4">
                <CardTitle className="text-[16px] font-semibold text-slate-900">
                  Status Update
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3.5 px-4 py-3.5">
                <div className="space-y-1.5">
                  <Label htmlFor="leadType" className="text-[11px] font-medium text-slate-700">
                    Lead Type
                  </Label>
                  <div className="relative">
                    <FileText className={fieldIconClass} />
                    <Select value={formData.leadType} onValueChange={(value) => setFormData((prev) => ({ ...prev, leadType: value }))}>
                      <SelectTrigger className={fieldSelectClass}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="Hot">Hot</SelectItem>
                        <SelectItem value="Warm">Warm</SelectItem>
                        <SelectItem value="Cold">Cold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="status" className="text-[11px] font-medium text-slate-700">
                    Status
                  </Label>
                  <div className="relative">
                    <CalendarDays className={fieldIconClass} />
                    <Select
                      value={formData.leadStatusId ? String(formData.leadStatusId) : ""}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, leadStatusId: Number(value) }))}
                    >
                      <SelectTrigger className={fieldSelectClass}>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {statusOptions.map((status) => (
                          <SelectItem key={status.id} value={String(status.id)}>
                            {status.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="remark" className="text-[11px] font-medium text-slate-700">
                    Add Remark
                  </Label>
                  <div className="relative">
                    <FileText className={fieldIconClass} />
                    <Textarea
                      id="remark"
                      value={formData.remark}
                      onChange={(e) => setFormData((prev) => ({ ...prev, remark: e.target.value }))}
                      placeholder="Add your notes here..."
                      rows={3}
                      className={fieldTextareaClass}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="nextFollowUp" className="text-[11px] font-medium text-slate-700">
                    Next Follow-up Date
                    {isFollowUpDateRequired() && <span className="ml-1 text-red-500">*</span>}
                  </Label>
                  <div className="relative">
                    <CalendarDays className={fieldIconClass} />
                    <Input
                      id="nextFollowUp"
                      type="date"
                      value={formData.nextFollowUp ? format(formData.nextFollowUp, "yyyy-MM-dd") : ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          nextFollowUp: e.target.value ? new Date(e.target.value) : undefined,
                        }))
                      }
                      className={fieldInputClass}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    className="h-9 rounded-[11px] border-slate-200 px-4 text-[13px] text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="h-9 rounded-[11px] bg-[#3f8b98] px-4 text-[13px] font-medium text-white shadow-[0_10px_18px_rgba(63,139,152,0.2)] hover:bg-[#4a99a6] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Check className="mr-2 h-3.5 w-3.5" />
                    {isSaving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {lead.interactions && lead.interactions.length > 0 && (
            <Card className="mt-3 overflow-hidden rounded-[18px] border border-slate-200 shadow-none">
              <CardHeader className="border-b border-slate-200 bg-white px-4 py-3">
                <CardTitle className="text-[16px] font-semibold text-slate-900">
                  Interaction History
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 py-3.5">
                <div className="space-y-2.5">
                  {lead.interactions.map((interaction) => (
                    <div
                      key={interaction.id}
                      className="flex gap-3 rounded-[12px] border border-slate-200 bg-white px-3.5 py-2.5"
                    >
                      <div className="mt-1 flex-shrink-0">
                        <div className="h-2 w-2 rounded-full bg-[#3f8b98]" />
                      </div>
                      <div className="flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="border-[#eadfc0] bg-[#fff8e8] text-[11px] text-[#8b7355]">
                            {typeof interaction.leadStatusId === "number" ? getStatusValue(interaction.leadStatusId) : "-"}
                          </Badge>
                          <div className="flex items-center gap-1 text-[11px] text-slate-500">
                            <Clock className="h-3 w-3" />
                            {new Date(interaction.date).toLocaleDateString("en-GB") + " at " + new Date(interaction.date).toLocaleTimeString()}
                          </div>
                          <span className="flex items-center gap-1 text-[11px] text-emerald-600">
                            <UserCheck className="h-3 w-3" />
                            {interaction?.firstName || "-"}
                          </span>
                        </div>
                        {interaction.reason && (
                          <p className="mb-1 text-[13px] text-slate-500">Reason: {interaction.reason}</p>
                        )}
                        {interaction.remark && (
                          <p className="text-[13px] text-slate-800">{interaction.remark}</p>
                        )}
                        {interaction.nextFollowUp && (
                          <p className="mt-1 text-[13px] text-[#8b7355]">
                            Next follow-up: {new Date(interaction.nextFollowUp).toLocaleDateString("en-GB")}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateLeadModal;
